import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Platform, View } from 'react-native';
import { Button, Card, CheckBox, Input, Text } from '@rneui/themed';
import { Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import { SelectField } from '../../common/SelectField';
import Utils from '../../../common/Utils';
import AppContext from '../../../contexts/App';
import AuthContext from '../../../contexts/Auth';
import GivingContext, { ActionTypes } from '../../../contexts/Giving';
import LayoutContext from '../../../contexts/Layout';
import Styles from './styles';

const ConstituentCard = props => {
    const { state: appState } = useContext(AppContext);
    const { state: authState } = useContext(AuthContext);
    const { state, dispatch } = useContext(GivingContext);
    const {
        layout: {
            dimensions: { width }
        }
    } = useContext(LayoutContext);
    const [recurrenceConfirmed, setRecurrenceConfirmed] = useState(false);
    const [addressOverridden, setAddressOverridden] = useState(false);
    const control = props?.control;
    const errors = props?.errors;
    const getValues = props?.getValues;
    const handleSubmit = props?.handleSubmit;
    const isValid = props?.isValid;
    const setValue = props?.setValue;
    const submit = props?.submit;
    const validateAddress = props?.validateAddress;
    const validateEmailDomain = props?.validateEmailDomain;
    const validatePhoneNumber = props?.validatePhoneNumber;
    const question1Text = props?.question1Text || 'PLEASE TELL US ABOUT YOURSELF';
    const recurrenceConfirmationText = props?.recurrenceConfirmationText;
    const clearErrors = props?.clearErrors;
    const hasRecurring = state.cartDetails.donations?.some(donation => donation.recurringType > 0);
    const addressAllowed = addressOverridden || state.cartDetails.addressInfo.accepted || state.cartDetails.addressInfo.verified;

    const Countries = useMemo(() => {
        if (!appState.countries) {
            return [];
        }

        const opts = [];

        for (const [key, value] of Object.entries(appState.countries)) {
            opts.push({
                label: value,
                value: key
            });
        }

        return opts;
    }, [appState.countries]);

    useEffect(() => {
        const { details, email, loggedIn } = authState.user;

        if (loggedIn) {
            setValue('firstName', details.given_name, { shouldTouch: true });
            setValue('lastName', details.family_name || details.lastName, { shouldTouch: true });
            setValue('address', details.address1, { shouldTouch: true });
            setValue('address2', details.address2, { shouldTouch: true });
            setValue('city', details.city, { shouldTouch: true });
            setValue('state', details.state, { shouldTouch: true });
            setValue('zip', details.postalCode, { shouldTouch: true });
            setValue('email', email, { shouldTouch: true });
            setValue('confirmEmail', email, { shouldTouch: true });
            setValue('mobile', details.phone_number, { shouldTouch: true });
        }
    }, [authState.user, setValue]);

    useEffect(() => {
        if (state.cartDetails.addressInfo.verified) {
            clearErrors(['address', 'city', 'state', 'zip']);
            setAddressOverridden(() => false);
        }
    }, [clearErrors, state.cartDetails.addressInfo.verified]);

    const resetAddressInputs = useCallback(() => {
        dispatch({
            type: ActionTypes.UPDATE_CART_DETAILS,
            data: {
                addressInfo: {
                    accepted: false,
                    verified: false,
                    details: undefined
                }
            }
        });

        setValue('address', '', { shouldDirty: true, shouldTouch: false, shouldValidate: true });
        setValue('address2', '', { shouldDirty: true, shouldTouch: false, shouldValidate: false });
        setValue('city', '', { shouldDirty: true, shouldTouch: false, shouldValidate: true });
        setValue('state', '', { shouldDirty: true, shouldTouch: false, shouldValidate: true });
        setValue('zip', '', { shouldDirty: true, shouldTouch: false, shouldValidate: true });
        setAddressOverridden(() => false);
    }, [dispatch, setValue]);

    return (
        <Card containerStyle={Styles.ConstituentCard?.container} wrapperStyle={{ width: '100%' }}>
            <Text
                style={{
                    fontFamily: 'GothamBook',
                    fontSize: !width <= 480 ? '15pt' : 15,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color: '#707070',
                    width: '100%',
                    textAlign: 'center',
                    marginVertical: 20
                }}
            >
                {question1Text}
            </Text>
            <View style={{ justifyContent: 'center', width: '100%' }}>
                <View style={{ flexDirection: 'row', width: '100%' }}>
                    <Controller
                        control={control}
                        defaultValue=''
                        name='firstName'
                        render={({ field, fieldState }) => {
                            return (
                                <Input
                                    {...field}
                                    errorMessage={
                                        !fieldState.error ? (
                                            <Text style={Styles.Form.label}>
                                                First Name
                                                <Text style={Styles.Form.asterisk}>*</Text>
                                            </Text>
                                        ) : (
                                            errors?.firstName?.message
                                        )
                                    }
                                    errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                    innerRef={field.ref}
                                    inputStyle={{
                                        ...Styles.Form.smallInputStyle,
                                        borderColor: 'white',
                                        borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#FF0000' : '#333333',
                                        borderWidth: 1
                                    }}
                                    onBlur={field.onBlur}
                                    onChangeText={val => field.onChange(val)}
                                    containerStyle={{ width: '50%' }}
                                    spellCheck={false}
                                    textContentType='name'
                                />
                            );
                        }}
                        rules={{
                            pattern: {
                                message: 'First name cannot contain numbers',
                                value: Utils.ValidNameRegExp
                            },
                            required: {
                                message: 'Please enter your first name.',
                                value: true
                            }
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue=''
                        name='lastName'
                        render={({ field, fieldState }) => {
                            return (
                                <Input
                                    {...field}
                                    containerStyle={{ width: '50%' }}
                                    errorMessage={
                                        !fieldState.error ? (
                                            <Text style={Styles.Form.label}>
                                                Last Name
                                                <Text style={Styles.Form.asterisk}>*</Text>
                                            </Text>
                                        ) : (
                                            errors?.lastName?.message
                                        )
                                    }
                                    errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                    innerRef={field.ref}
                                    inputStyle={{
                                        ...Styles.Form.smallInputStyle,
                                        borderColor: 'white',
                                        borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#FF0000' : '#333333',
                                        borderWidth: 1
                                    }}
                                    onBlur={field.onBlur}
                                    onChangeText={val => field.onChange(val)}
                                    textContentType='name'
                                    spellCheck={false}
                                />
                            );
                        }}
                        rules={{
                            pattern: {
                                message: 'Last name cannot contain numbers',
                                value: Utils.ValidNameRegExp
                            },
                            required: {
                                message: 'Please enter your last name.',
                                value: true
                            }
                        }}
                    />
                </View>
                <View style={{ flexDirection: 'row', marginTop: '8pt', width: '100%' }}>
                    <Controller
                        control={control}
                        defaultValue=''
                        name='email'
                        render={({ field, fieldState }) => {
                            return (
                                <Input
                                    {...field}
                                    containerStyle={{ width: '50%' }}
                                    errorMessage={
                                        !fieldState.error ? (
                                            <Text style={Styles.Form.label}>
                                                Email Address
                                                <Text style={Styles.Form.asterisk}>*</Text>
                                            </Text>
                                        ) : (
                                            errors?.email?.message
                                        )
                                    }
                                    errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                    innerRef={field.ref}
                                    inputStyle={{
                                        ...Styles.Form.smallInputStyle,
                                        borderColor: 'white',
                                        borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#FF0000' : '#333333',
                                        borderWidth: 1
                                    }}
                                    onBlur={field.onBlur}
                                    spellCheck={false}
                                    textContentType='emailAddress'
                                />
                            );
                        }}
                        rules={{
                            required: {
                                message: 'Please enter your email address.',
                                value: true
                            },
                            pattern: {
                                message: 'Not a valid email address',
                                value: Utils.ValidEmailRegExp
                            },
                            validate: async () => await validateEmailDomain()
                        }}
                    />
                    <Controller
                        control={control}
                        defaultValue=''
                        name='confirmEmail'
                        render={({ field, fieldState }) => {
                            return (
                                <Input
                                    {...field}
                                    containerStyle={{ width: '50%' }}
                                    errorMessage={
                                        !fieldState.error ? (
                                            <Text style={Styles.Form.label}>
                                                Verify Email Address
                                                <Text style={Styles.Form.asterisk}>*</Text>
                                            </Text>
                                        ) : (
                                            errors?.confirmEmail?.message
                                        )
                                    }
                                    errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                    innerRef={field.ref}
                                    inputStyle={{
                                        ...Styles.Form.smallInputStyle,
                                        borderColor: 'white',
                                        borderBottomColor: fieldState.isTouched && fieldState.invalid ? '#FF0000' : '#333333',
                                        borderWidth: 1
                                    }}
                                    onBlur={field.onBlur}
                                    onChangeText={val => field.onChange(val)}
                                    textContentType='emailAddress'
                                    spellCheck={false}
                                />
                            );
                        }}
                        rules={{
                            required: {
                                message: 'Please verify you email address.',
                                value: true
                            },
                            validate: () => {
                                const email = getValues('email');
                                const confirmEmail = getValues('confirmEmail');

                                return email?.equals(confirmEmail.toLowerCase()) || 'Emails do not match';
                            }
                        }}
                    />
                </View>
            </View>
            <View style={{ flexDirection: 'row', marginTop: '8pt' }}>
                <Controller
                    control={control}
                    defaultValue='US'
                    name='country'
                    render={({ field }) => (
                        <View style={{ marginLeft: 10, width: '100%' }}>
                            <SelectField
                                {...field}
                                blur={field.blur}
                                containerStyle={{
                                    borderBottomWidth: 1,
                                    borderBottomColor: '#333333',
                                    width: '98%',
                                    paddingTop: Platform.OS === 'web' ? '4pt' : 4,
                                    paddingBottom: Platform.OS === 'web' ? '2pt' : 2
                                }}
                                innerRef={field.ref}
                                onValueChange={val => {
                                    resetAddressInputs();

                                    field.onChange(val);
                                }}
                                options={Countries}
                                prompt='Country'
                                style={{
                                    ...Styles.Form.smallInputStyle,
                                    border: 0,
                                    maxHeight: 40,
                                    width: undefined,
                                    backgroundColor: '#fff',
                                    paddingLeft: 0
                                }}
                            />
                            <Text style={[Styles.Form.label, { paddingLeft: '3pt' }]}>
                                Country
                                <Text style={Styles.Form.asterisk}>*</Text>
                            </Text>
                        </View>
                    )}
                    rules={{
                        required: {
                            message: 'Country is required',
                            value: true
                        }
                    }}
                />
            </View>
            <View style={{ marginTop: '8pt' }}>
                {(state.cartDetails.addressInfo?.details || addressOverridden) && (
                    <View style={{ flexDirection: !state.cartDetails?.addressInfo?.verified ? 'row' : 'column' }}>
                        <Text
                            style={{
                                ...Styles.ConstituentCard.addressVerifiedIndicator,
                                backgroundColor: addressOverridden ? '#c1a638' : state.cartDetails?.addressInfo?.verified ? '#008000' : '#a61a2b',
                                borderColor: addressOverridden ? '#a38e3a' : state.cartDetails?.addressInfo?.verified ? '#008000' : '#a61a2b',
                                width: addressOverridden || !state.cartDetails?.addressInfo?.verified ? '15%' : '10%'
                            }}
                        >
                            {addressOverridden ? 'User Verified' : state.cartDetails?.addressInfo?.verified ? 'Verified' : 'Not Verified'}
                        </Text>
                        {!state.cartDetails?.addressInfo?.verified && !addressOverridden && (
                            <Text
                                onPress={() => setAddressOverridden(true)}
                                style={{
                                    ...Styles.ConstituentCard.addressVerifiedIndicator,
                                    borderWidth: 0,
                                    color: '#6b0410',
                                    marginLeft: 0,
                                    textDecorationLine: 'underline'
                                }}
                            >
                                Use anyway
                            </Text>
                        )}
                    </View>
                )}
                <Controller
                    control={control}
                    defaultValue=''
                    name='address'
                    render={({ field, fieldState }) => {
                        return (
                            <Input
                                {...field}
                                containerStyle={{ marginTop: '8pt', width: '100%' }}
                                errorMessage={
                                    !fieldState.error ? (
                                        <Text style={Styles.Form.label}>
                                            Address Line 1<Text style={Styles.Form.asterisk}>*</Text>
                                        </Text>
                                    ) : (
                                        errors?.address?.message
                                    )
                                }
                                errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                maxLength={50}
                                onBlur={field.onBlur}
                                innerRef={field.ref}
                                placeholderTextColor={'#707070'}
                                textContentType='streetAddressLine1'
                                spellCheck={false}
                                inputStyle={{
                                    ...Styles.Form.smallInputStyle,
                                    borderColor: 'white',
                                    borderBottomColor: fieldState.error || fieldState.invalid ? '#FF0000' : '#333333',
                                    borderWidth: 1
                                }}
                            />
                        );
                    }}
                    rules={{
                        required: {
                            message: 'Please enter your address.',
                            value: true
                        },
                        validate: async () => {
                            setAddressOverridden(() => false);
                            await validateAddress();
                        }
                    }}
                />
            </View>

            <Controller
                control={control}
                defaultValue=''
                name='address2'
                render={({ field }) => {
                    return (
                        <Input
                            {...field}
                            containerStyle={{ marginTop: '8pt', width: '100%' }}
                            errorMessage='Address Line 2'
                            errorStyle={{ ...Styles.Form.placeholderStyle, color: '#000' }}
                            onBlur={field.onBlur}
                            onChangeText={val => field.onChange(val)}
                            inputContainerStyle={{ width: '100%' }}
                            innerRef={field.ref}
                            inputStyle={{
                                ...Styles.Form.smallInputStyle,
                                borderColor: 'white',
                                borderBottomColor: '#333333',
                                borderWidth: 1
                            }}
                            maxLength={50}
                            textContentType='streetAddressLine2'
                            spellCheck={false}
                        />
                    );
                }}
                rules={{
                    validate: async () => {
                        setAddressOverridden(() => false);
                        await validateAddress();
                    }
                }}
            />
            <View style={{ flexDirection: 'row', marginTop: '8pt', width: '100%' }}>
                <Controller
                    control={control}
                    defaultValue=''
                    name='city'
                    render={({ field, fieldState }) => (
                        <Input
                            {...field}
                            containerStyle={{ maxWidth: '33%', padding: 0 }}
                            errorMessage={
                                !fieldState.error || addressAllowed ? (
                                    <Text style={Styles.Form.label}>
                                        City/Region
                                        <Text style={Styles.Form.asterisk}>*</Text>
                                    </Text>
                                ) : (
                                    errors?.city?.message
                                )
                            }
                            errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                            innerRef={field.ref}
                            inputStyle={{
                                ...Styles.Form.smallInputStyle,
                                borderColor: 'white',
                                borderBottomColor: fieldState.error || fieldState.invalid ? '#FF0000' : '#333333',
                                borderWidth: 1,
                                overflow: 'hidden'
                            }}
                            maxLength={30}
                            onBlur={field.onBlur}
                            onChangeText={val => field.onChange(val)}
                            textContentType='addressCity'
                            spellCheck={false}
                        />
                    )}
                    rules={{
                        pattern: {
                            message: 'City name cannot contain numerics',
                            value: Utils.ValidCityStateNameRegExp
                        }
                    }}
                />
                <Controller
                    control={control}
                    defaultValue=''
                    name='state'
                    render={({ field, fieldState }) => {
                        return (
                            <Input
                                {...field}
                                containerStyle={{ maxWidth: '33%', padding: 0 }}
                                errorMessage={
                                    !fieldState.error ? (
                                        <Text style={Styles.Form.label}>
                                            {getValues('country') === 'CA' ? 'Province' : 'State'}
                                            <Text style={Styles.Form.asterisk}>*</Text>
                                        </Text>
                                    ) : (
                                        errors?.state?.message
                                    )
                                }
                                errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                innerRef={field.ref}
                                inputStyle={{
                                    ...Styles.Form.smallInputStyle,
                                    borderColor: 'white',
                                    borderBottomColor: fieldState.error || fieldState.invalid ? '#FF0000' : '#333333',
                                    borderWidth: 1,
                                    overflow: 'hidden'
                                }}
                                maxLength={30}
                                onBlur={field.onBlur}
                                onChangeText={val => field.onChange(val)}
                                textContentType='addressState'
                                spellCheck={false}
                            />
                        );
                    }}
                    rules={{
                        pattern: {
                            message: `${getValues('country') === 'CA' ? 'Province' : 'State'} name cannot contain numerics`,
                            value: Utils.ValidCityStateNameRegExp
                        }
                    }}
                />
                <Controller
                    control={control}
                    defaultValue=''
                    name='zip'
                    render={({ field, fieldState }) => {
                        return (
                            <Input
                                {...field}
                                containerStyle={{ maxWidth: '33%', padding: 0 }}
                                errorMessage={
                                    !fieldState.error ? (
                                        <Text style={Styles.Form.label}>
                                            Postal Code
                                            <Text style={Styles.Form.asterisk}>*</Text>
                                        </Text>
                                    ) : (
                                        errors?.zip?.message
                                    )
                                }
                                errorStyle={!fieldState.error ? Styles.Form.placeholderStyle : Styles.Form.errorStyle}
                                innerRef={field.ref}
                                inputStyle={{
                                    ...Styles.Form.smallInputStyle,
                                    borderColor: 'white',
                                    borderBottomColor: fieldState.error || fieldState.invalid ? '#FF0000' : '#333333',
                                    borderWidth: 1
                                }}
                                onBlur={field.onBlur}
                                onChangeText={val => field.onChange(val)}
                                textContentType='postalCode'
                                spellCheck={false}
                            />
                        );
                    }}
                    rules={{
                        required: {
                            message: 'Please enter a postal code.',
                            value: true
                        },
                        validate: async () => {
                            setAddressOverridden(() => false);
                            await validateAddress();
                        }
                    }}
                />
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'center', marginTop: '8pt', width: '100%' }}>
                <Controller
                    control={control}
                    defaultValue=''
                    name='mobile'
                    render={({ field, fieldState }) => {
                        return (
                            <Input
                                {...field}
                                errorMessage={!fieldState.error ? <Text style={Styles.Form.label}>Mobile Phone</Text> : errors?.mobile?.message}
                                errorStyle={!fieldState.error ? { ...Styles.Form.placeholderStyle, color: '#333333' } : Styles.Form.errorStyle}
                                innerRef={field.ref}
                                inputStyle={{
                                    ...Styles.Form.smallInputStyle,
                                    borderColor: 'white',
                                    borderBottomColor: '#333333',
                                    borderWidth: 1
                                }}
                                onBlur={field.onBlur}
                                textContentType='telephoneNumber'
                                maxLength={15}
                                placeholderTextColor={'#333333'}
                                spellCheck={false}
                            />
                        );
                    }}
                    rules={{
                        validate: async () => await validatePhoneNumber()
                    }}
                />
            </View>
            <View style={Styles.ConstituentCard.confirmationContainer}>
                {!hasRecurring && <Text style={Styles.ConstituentCard.confirmationText}>Your one time payment will be processed upon submission.</Text>}
                {hasRecurring && (
                    <CheckBox
                        center={true}
                        checked={recurrenceConfirmed}
                        checkedColor={Styles.ConstituentCard.confirmationCheckbox?.color}
                        containerStyle={{ justifyContent: 'flex-start', width: '80%' }}
                        textStyle={Styles.ConstituentCard.confirmationCheckbox?.text}
                        title={recurrenceConfirmationText.replaceAll('${type}', state.cartDetails.paymentInfo.method)}
                        onIconPress={() => setRecurrenceConfirmed(!recurrenceConfirmed)}
                    />
                )}
            </View>
            <Button
                buttonStyle={{
                    ...Styles.ConstituentCard.donationButton,
                    backgroundColor: '#FE0000'
                }}
                containerStyle={Styles.ConstituentCard.donationButtonContainer}
                disabled={!isValid || (hasRecurring && !recurrenceConfirmed) || !addressAllowed}
                disabledStyle={Styles.ConstituentCard.donationButtonDisabled}
                disabledTitleStyle={Styles.ConstituentCard.donationButtonDisabledTitle}
                onPress={handleSubmit(submit)}
                title='Give Now'
                titleStyle={{
                    color: '#FFFFFF',
                    fontWeight: 900,
                    fontFamily: 'GothamMedium',
                    fontSize: Platform.OS.equals('web') ? '26pt' : 26
                }}
                type='solid'
            />
        </Card>
    );
};

ConstituentCard.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    getValues: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    isValid: PropTypes.bool.isRequired,
    json: PropTypes.shape({
        question1Text: PropTypes.string,
        recurrenceConfirmationText: PropTypes.string
    }),
    setValue: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired,
    validateAddress: PropTypes.func.isRequired,
    validateEmailDomain: PropTypes.func.isRequired,
    validatePhoneNumber: PropTypes.func.isRequired
};

export default ConstituentCard;
